import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Input, Text, Button, chakra, useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeft, Search } from 'lucide-react';
import { NavBar, pillTitleStyles } from '@/components/ui';
import { ChatThread } from '@/components/chat/ChatThread';
import { ChatAvatar } from '@/components/chat/ChatAvatar';
import type { ChatMessage } from '@/components/chat/types';
import EventPreviewModal, { type ProfileEvent } from '@/components/common/EventPreviewModal';
import {
  DepartmentCarousel,
  EventSelector,
  GeneralChatCard,
  ChatMenu,
  NewDirectChatModal,
  PrivateMessageRow,
  SectionLabel,
} from './components';
import { PinnedMessageBanner } from './components/PinnedMessageBanner';
import { PinnedMessagesSheet } from './components/PinnedMessagesSheet';
import { useChatPins, type ChatPin } from '@/hooks/useChatPins';
import { useCommunicationPage, type ChatListItem } from './useCommunicationPage';

export function CommunicationPage() {
  const navigate = useNavigate();

  const {
    isOrgView,
    effectiveOrgId,
    events,
    selectedEventId,
    selectEvent,
    generalChat,
    departmentSection,
    directSection,
    selected,
    selectedKey,
    setSelectedKey,
    listLoading,
    error,
    searchQuery,
    setSearchQuery,
    handleStartDirectChat,
    handleMessageSent,
    refetchEventChats,
    refetchDirectChats,
    directChatsById,
    mutedUntil,
    mute,
    unmute,
    ownedEventIds,
    userId,
  } = useCommunicationPage();

  const [chatOpen, setChatOpen] = useState(false);
  const [pendingMobileClose, setPendingMobileClose] = useState(false);
  /**
   * Whether this page opened with a thread already selected, and whether the slide-in has
   * been spent. Together they let the first, restored thread appear without animating
   * while every thread opened from the list afterwards still slides in.
   */
  const restoredFromUrlRef = useRef(selectedKey != null);
  const hasAnimatedOpenRef = useRef(false);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<ProfileEvent | null>(null);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [pinListOpen, setPinListOpen] = useState(false);
  /** A "jump to this message" request handed to ChatThread, cleared once it has run. */
  const [focusMessage, setFocusMessage] = useState<{ id: string; createdAt: string } | null>(
    null
  );

  // The client-side twin of is_event_org_admin(): every thread kind carries the event it
  // belongs to, and ownedEventIds is the set this viewer runs as the org they are acting
  // as. RLS is still the authority -- this only decides what to offer.
  const canPin = selected != null && ownedEventIds.has(selected.eventId);
  const {
    pins,
    isPinned,
    pin: pinMessage,
    unpin: unpinMessage,
  } = useChatPins(selected?.target ?? null, canPin);

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }) ?? false;
  const useSinglePaneLayout = isMobile || isTablet;

  const searching = searchQuery.trim().length > 0;
  const hasAnything = generalChat != null || departmentSection.length > 0 || directSection.length > 0;

  const openEventDetails = () => {
    if (!selected) return;
    setPreviewEvent({
      id: selected.eventId,
      name: selected.eventName ?? 'Event',
      location: '',
      image: selected.eventImage ?? null,
    });
    setShowEventPreview(true);
  };

  // Drive single-pane slide-in / slide-out based on selection.
  useEffect(() => {
    if (!useSinglePaneLayout) {
      setChatOpen(false);
      setPendingMobileClose(false);
      return;
    }
    if (selectedKey) {
      // A thread the URL already named -- someone coming back from a profile -- was never
      // "opened" from the list, so sliding it in would replay a transition the user did
      // not ask for. Mark it open before the pane mounts and it simply appears; a CSS
      // transition does not run on a first render.
      if (!hasAnimatedOpenRef.current && restoredFromUrlRef.current) {
        hasAnimatedOpenRef.current = true;
        setPendingMobileClose(false);
        setChatOpen(true);
        return;
      }
      hasAnimatedOpenRef.current = true;
      // Start from off-screen, then slide in on the next frame
      setChatOpen(false);
      setPendingMobileClose(false);
      requestAnimationFrame(() => setChatOpen(true));
    } else {
      setChatOpen(false);
      setPendingMobileClose(false);
    }
  }, [useSinglePaneLayout, selectedKey]);

  const clearSelection = () => {
    setSelectedKey(null);
  };

  const handleMobileBack = () => {
    if (!useSinglePaneLayout) {
      clearSelection();
      return;
    }

    // Start slide-out animation; clear selection on transition end.
    setPendingMobileClose(true);
    setChatOpen(false);
  };

  const select = (item: ChatListItem) => setSelectedKey(item.key);

  const chatHeader = selected ? (
    <Flex
      as="header"
      h="73px"
      shrink={0}
      align="center"
      justify="space-between"
      px={{ base: 3, md: 6 }}
      borderBottomWidth={1}
      borderColor="gray.200"
      shadow="sm"
      zIndex={10}
    >
      <chakra.button
        type="button"
        aria-label="Back to chat list"
        display={useSinglePaneLayout ? 'flex' : 'none'}
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        w="40px"
        h="40px"
        ml={-2}
        mr={1}
        borderRadius="full"
        bg="transparent"
        color="volyx.primary"
        cursor="pointer"
        transition="background 0.15s, transform 0.15s"
        _hover={{ bg: 'volyx.lightGray' }}
        _active={{ transform: 'scale(0.92)' }}
        onClick={handleMobileBack}
      >
        <ChevronLeft size={26} strokeWidth={2.5} />
      </chakra.button>
      <Flex
        align="center"
        gap={3}
        minW={0}
        flex={1}
        overflow="hidden"
        cursor="pointer"
        onClick={openEventDetails}
        _hover={{ opacity: 0.85 }}
      >
        <ChatAvatar kind={selected.kind} stored={selected.image} name={selected.title} size="md" />
        <Box minW={0} flex={1}>
          <Text fontSize="md" fontWeight="semibold" color="gray.800" lineClamp={1}>
            {selected.title}
          </Text>
          {selected.subtitle ? (
            <Text fontSize="xs" color="gray.500" lineClamp={1}>
              {selected.subtitle}
            </Text>
          ) : null}
        </Box>
      </Flex>
      <ChatMenu
        muted={selected.muted}
        mutedUntil={mutedUntil(selected.key)}
        onMute={(duration) => void mute(selected.target, duration)}
        onUnmute={() => void unmute(selected.target)}
        onOpenDetails={openEventDetails}
      />
    </Flex>
  ) : null;

  const chatBody = selected ? (
    <Flex flex={1} flexDirection="column" minH={0} overflow="hidden">
      <ChatThread
        target={selected.target}
        directParticipantIds={
          selected.target.kind === 'direct'
            ? (() => {
                const c = directChatsById.get(selected.target.directChatId);
                return c ? [c.volunteerId, c.createdBy] : undefined;
              })()
            : undefined
        }
        onMessageSent={(message: ChatMessage) => handleMessageSent(selected, message)}
        // Naming the thread means coming back from a profile reopens the conversation
        // rather than the chat list -- selection is page state, so the URL has to carry
        // it. Same two parameters a chat notification links with.
        profileReturnTo={`/communication?${new URLSearchParams({
          event: selected.eventId,
          chat: selected.key,
        }).toString()}`}
        canPin={canPin}
        isMessagePinned={isPinned}
        onPinMessage={(message: ChatMessage) => {
          if (userId) void pinMessage(message, userId);
        }}
        onUnpinMessage={(messageId: string) => void unpinMessage(messageId)}
        focusMessage={focusMessage}
        onMessageFocused={() => setFocusMessage(null)}
      />
    </Flex>
  ) : null;

  const jumpToPin = (pin: ChatPin) => {
    if (!pin.message) return;
    setPinListOpen(false);
    setFocusMessage({ id: pin.message_id, createdAt: pin.message.created_at });
  };

  const pinnedBanner = selected ? (
    <PinnedMessageBanner
      pins={pins}
      onJumpTo={jumpToPin}
      onOpenList={() => setPinListOpen(true)}
    />
  ) : null;

  const sections = (
    <>
      {generalChat ? (
        <GeneralChatCard
          item={generalChat}
          active={selectedKey === generalChat.key}
          onSelect={() => select(generalChat)}
        />
      ) : null}

      {/* Hidden entirely when the event declares no departments, rather than showing an
          empty heading. While searching, a section with no matches disappears too. */}
      {departmentSection.length > 0 ? (
        <>
          <SectionLabel>{isOrgView ? 'Departments' : 'My departments'}</SectionLabel>
          <DepartmentCarousel
            items={departmentSection}
            selectedKey={selectedKey}
            onSelect={select}
          />
        </>
      ) : null}

      {/* The private section keeps its heading even when empty, because the "+" that opens
          a new thread lives on it -- but not while searching, where an empty section is
          just noise. */}
      {directSection.length > 0 || (isOrgView && effectiveOrgId && !searching) ? (
        <>
          <SectionLabel
            action={
              isOrgView && effectiveOrgId ? (
                <Button
                  type="button"
                  variant="ghost"
                  colorPalette="blue"
                  // 44px is the minimum comfortable touch target on iOS; the default ghost
                  // button was well under it, so the glyph was bigger than its hit area.
                  minW="44px"
                  h="44px"
                  p={0}
                  mr={-2}
                  borderRadius="full"
                  fontSize="2xl"
                  fontWeight="normal"
                  lineHeight="1"
                  aria-label="New message"
                  _active={{ transform: 'scale(0.92)' }}
                  transition="transform 0.12s ease-out"
                  onClick={() => setNewChatOpen(true)}
                >
                  +
                </Button>
              ) : null
            }
          >
            Private messages
          </SectionLabel>
          {directSection.length > 0 ? (
            directSection.map((item) => (
              <PrivateMessageRow
                key={item.key}
                item={item}
                active={selectedKey === item.key}
                onSelect={() => select(item)}
              />
            ))
          ) : (
            <Text px={4} pb={2} fontSize="sm" color="gray.500">
              No private messages yet.
            </Text>
          )}
        </>
      ) : null}
    </>
  );

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      flexDirection="column"
      minH={0}
      bg={useSinglePaneLayout && selected ? 'white' : 'gray.50'}
      // Fixed element must consume its own safe-area insets
      pt="env(safe-area-inset-top, 0px)"
      pb={{ base: selected ? 0 : 'var(--mobile-bottom-safe)', lg: 0 }}
      overflow="hidden"
      pl={{ base: 0, lg: 'var(--sidebar-width)' }}
      transitionProperty={{ lg: 'padding-left' }}
      transitionDuration={{ lg: '300ms' }}
      transitionTimingFunction={{ lg: 'ease-in-out' }}
    >
      <Flex flex={1} minH={0} overflow="hidden" align="stretch">
        {/* Sidebar: single-pane on mobile/tablet, split view on large desktop */}
        <Box
          as="aside"
          // 320px was a phone-width column on a laptop. The list is the primary surface
          // when no thread is open, so it gets real width and grows with the viewport.
          w={{ base: '100%', lg: '380px', xl: '440px' }}
          flexShrink={0}
          minH={0}
          borderRightWidth={1}
          borderColor="gray.200"
          bg="gray.50"
          display={useSinglePaneLayout && selected ? 'none' : 'flex'}
          flexDirection="column"
          overflow="hidden"
        >
          <Flex
            px={4}
            pb={4}
            // The root already consumes env(safe-area-inset-top). Measured on iPhone 17:
            // the Dynamic Island ends at 51pt, and 20px here puts the pill top at ~82pt —
            // about 31pt of clearance. Touch only; desktop has no notch to clear.
            pt={{ base: 5, lg: 4 }}
            borderBottomWidth={1}
            borderColor="gray.200"
            align="center"
            justify="space-between"
            gap={2}
          >
            <Box
              {...pillTitleStyles}
              fontSize={{ base: 'lg', md: 'xl' }}
              flexShrink={0}
            >
              Messages
            </Box>
            <EventSelector
              events={events}
              selectedId={selectedEventId}
              onSelect={selectEvent}
            />
          </Flex>

          {events.length > 0 ? (
            <Box px={4} pt={3}>
              <Flex position="relative" align="center">
                <Box
                  position="absolute"
                  left={3}
                  color="volyx.textSecondary"
                  pointerEvents="none"
                  display="flex"
                >
                  <Search size={16} />
                </Box>
                <Input
                  id="communication-search-chats"
                  name="search-chats"
                  placeholder="Search a conversation or a volunteer"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="sm"
                  pl={9}
                  h="38px"
                  borderRadius="full"
                  borderWidth={0}
                  bg="volyx.lightGray"
                  _focus={{ boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                />
              </Flex>
            </Box>
          ) : null}

          <Box flex={1} overflowY="auto" minH={0} pb={4}>
            {listLoading ? (
              <Text p={4} fontSize="sm" color="gray.500">
                Loading chats…
              </Text>
            ) : error ? (
              <Text p={4} fontSize="sm" color="red.500">
                {error}
              </Text>
            ) : events.length === 0 ? (
              <Text p={4} fontSize="sm" color="gray.500">
                No events yet. Chats appear here once you create or join one.
              </Text>
            ) : !hasAnything ? (
              <Text p={4} fontSize="sm" color="gray.500">
                {searching
                  ? `No conversations match “${searchQuery.trim()}”.`
                  : 'No chats for this event yet.'}
              </Text>
            ) : (
              sections
            )}
          </Box>
        </Box>

        {/* Main chat area: single-pane on mobile/tablet, split view on large desktop */}
        <Box
          as="main"
          flex={1}
          display={useSinglePaneLayout ? (selected ? 'flex' : 'none') : 'flex'}
          flexDirection="column"
          minW={0}
          minH={0}
          overflow="hidden"
          bg="white"
        >
          {selected ? (
            useSinglePaneLayout ? (
              <Box
                w="100%"
                h="100%"
                display="flex"
                flexDirection="column"
                transform={chatOpen ? 'translateX(0%)' : 'translateX(100%)'}
                transition="transform 0.3s ease-in-out"
                onTransitionEnd={(e) => {
                  if (!pendingMobileClose) return;
                  if (e.propertyName !== 'transform') return;
                  if (chatOpen) return;
                  setPendingMobileClose(false);
                  clearSelection();
                }}
              >
                {chatHeader}
                {pinnedBanner}
                {chatBody}
              </Box>
            ) : (
              <>
                {chatHeader}
                {pinnedBanner}
                {chatBody}
              </>
            )
          ) : (
            <Flex
              flex={1}
              direction="column"
              align="center"
              justify="center"
              gap={2}
              px={8}
              color="gray.500"
            >
              <Text fontSize="md" fontWeight="semibold" color="gray.600">
                No conversation open
              </Text>
              <Text fontSize="sm" textAlign="center" maxW="320px">
                Pick the general chat, a department or a private message to start reading.
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>
      {/* Hide bottom nav on mobile while chat is open */}
      {isMobile && selected ? null : <NavBar />}

      {isOrgView && effectiveOrgId ? (
        <NewDirectChatModal
          open={newChatOpen}
          organizationId={effectiveOrgId}
          onClose={() => setNewChatOpen(false)}
          onPick={(eventId, volunteerId) => {
            setNewChatOpen(false);
            void handleStartDirectChat(eventId, volunteerId);
          }}
        />
      ) : null}

      <PinnedMessagesSheet
        open={pinListOpen}
        onClose={() => setPinListOpen(false)}
        pins={pins}
        canPin={canPin}
        onUnpin={(messageId) => void unpinMessage(messageId)}
        onJumpTo={jumpToPin}
      />

      <EventPreviewModal
        visible={showEventPreview}
        onClose={() => setShowEventPreview(false)}
        isOrganization={false}
        hideOrganizationInfo={false}
        event={previewEvent}
        onJoinEvent={() => {
          setShowEventPreview(false);
        }}
        onOpenVolunteerTasks={(eventId) =>
          navigate(`/volunteer-tasks?event=${encodeURIComponent(eventId)}`)
        }
        onLeftEvent={() => {
          setShowEventPreview(false);
          // The private thread with the org is scoped to the event too, so it goes with it.
          refetchEventChats();
          void refetchDirectChats();
        }}
      />
    </Box>
  );
}
